/**
 * App Configuration
 *
 * Centralizes environment-dependent settings.
 * In development, falls back to localhost:8080.
 * In production, set REACT_APP_API_URL and REACT_APP_WS_URL via env vars.
 */

const defaultApiUrl = `${window.location.protocol}//${window.location.hostname}:8080`;

export const API_URL = process.env.REACT_APP_API_URL || defaultApiUrl;

const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const defaultWsBase = `${wsProtocol}//${window.location.hostname}:8080`;

// Derive WS URL from API_URL if REACT_APP_WS_URL not set
function deriveWsUrl() {
  if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL
      .replace(/^https:/, 'wss:')
      .replace(/^http:/, 'ws:');
  }
  return defaultWsBase;
}

export const WS_URL = deriveWsUrl();

// Best-effort cleanup of any legacy admin-key sessionStorage entries from
// previous app versions — safe to remove this helper after a few weeks.
try {
  sessionStorage.removeItem('flashcards_admin_key');
} catch { /* sessionStorage may be unavailable in some embeds */ }

// JWT token helpers — stored in localStorage to persist across sessions.
//
// Two parallel session "slots" coexist on the same device so a teacher who
// signed in with their email doesn't lose their session if their child joins
// a class on the same browser:
//   - flashcards_token / flashcards_teacher  → teacher session
//   - jiayou_student_token / jiayou_student_session → student class session
//
// `flashcards_session_type` (`teacher` | `student` | empty) records which one
// is the *active* session. Apollo and AuthContext follow that pointer.
const TOKEN_KEY = 'flashcards_token';
const TEACHER_KEY = 'flashcards_teacher';
const STUDENT_TOKEN_KEY = 'jiayou_student_token';
const STUDENT_SESSION_KEY = 'jiayou_student_session';
const SESSION_TYPE_KEY = 'flashcards_session_type';

export const SESSION_TYPE_TEACHER = 'teacher';
export const SESSION_TYPE_STUDENT = 'student';

export function getSessionType() {
  return localStorage.getItem(SESSION_TYPE_KEY) || '';
}

function setSessionType(type) {
  if (type) localStorage.setItem(SESSION_TYPE_KEY, type);
  else localStorage.removeItem(SESSION_TYPE_KEY);
}

// Returns whichever JWT corresponds to the active session — used by Apollo
// and any plain fetch() that needs an Authorization header.
export function getToken() {
  if (getSessionType() === SESSION_TYPE_STUDENT) {
    return localStorage.getItem(STUDENT_TOKEN_KEY);
  }
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
  setSessionType(SESSION_TYPE_TEACHER);
}

export function getStoredTeacher() {
  const raw = localStorage.getItem(TEACHER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function setStoredTeacher(teacher) {
  localStorage.setItem(TEACHER_KEY, JSON.stringify(teacher));
}

// Persist the student session locally and switch the active session to student.
export function setStudentSession(token, session) {
  localStorage.setItem(STUDENT_TOKEN_KEY, token);
  localStorage.setItem(STUDENT_SESSION_KEY, JSON.stringify(session));
  setSessionType(SESSION_TYPE_STUDENT);
}

export function getStudentSession() {
  const raw = localStorage.getItem(STUDENT_SESSION_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function getStudentToken() {
  return localStorage.getItem(STUDENT_TOKEN_KEY);
}

export function clearStudentSession() {
  localStorage.removeItem(STUDENT_TOKEN_KEY);
  localStorage.removeItem(STUDENT_SESSION_KEY);
  if (getSessionType() === SESSION_TYPE_STUDENT) setSessionType('');
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TEACHER_KEY);
  if (getSessionType() === SESSION_TYPE_TEACHER) setSessionType('');
}
